import { req } from "../api.ts";
import { navigate } from "../router.ts";
import { onDisconnect } from "../ui/lifecycle.ts";
import { ensurePlayer, readPlayer, PlayConnection, PlayJournal, commandId, outcomeMessage, sessionRecovery, type Player, type Seek } from "../play/client.ts";
import { playPage } from "./play.ts";
import { playerPreferences } from "../play/profile.ts";

export function clubScreen(): HTMLElement {
  const root = playPage(`<h1>Find a player</h1><p>A quiet table for a game. Join when you can stay and play.</p>
    <p class="fine">Standard chess · no clock · unrated. Searching lasts up to ten minutes.</p>
    <button class="btn primary" data-find>I'm available to play</button><button class="btn ghost" data-cancel hidden>Cancel search</button>
    <p data-status role="status" aria-live="polite">You are not looking for a game yet.</p><div data-players></div><p class="msg" role="status" aria-live="polite"></p>`,"players");
  let player: Player | undefined; let mine: Seek | null = null; let busy = false; let dead = false; let loading = false;
  let starting: Promise<void> | undefined;
  let hiddenSearch = false;
  const find = root.querySelector<HTMLButtonElement>("[data-find]")!;
  const cancel = root.querySelector<HTMLButtonElement>("[data-cancel]")!;
  const message = root.querySelector<HTMLElement>(".msg")!;
  root.querySelector('main')!.append(playerPreferences(()=>void refresh()));
  const connection = new PlayConnection(() => { render(); void refresh(); },() => void refresh());
  function establish(): Promise<void> {
    return starting ??= ensurePlayer().then(p => {
      if (!dead) { player = p; connection.start(); }
    }).finally(() => { starting = undefined; });
  }
  function render() {
    find.disabled = busy || (player !== undefined && !connection.id); find.hidden = mine?.state === "waiting" || mine?.state === "reserved";
    cancel.hidden = !find.hidden; cancel.disabled = busy;
    root.querySelectorAll<HTMLButtonElement>("[data-seek]").forEach(button => button.disabled = busy || (player !== undefined && !connection.id) || mine?.state === "waiting");
    root.querySelector("[data-status]")!.textContent = mine?.state === "waiting" ? "Looking for another available player…" : mine?.state === "reserved" ? "A player is ready. Opening your board…" : player && !connection.id ? connection.error : hiddenSearch ? "Your search stopped while this tab was hidden. Select I’m available to search again." : "You are not looking for a game yet.";
  }
  async function refresh() {
    if (dead || loading) return;
    loading = true;
    try {
      const result = await req<{mine:Seek|null;seeks:Seek[]}>("GET","/api/play/seeks");
      if (dead) return;
      mine = result.mine;
      if (mine?.state === "reserved" && mine.slot_id) { navigate(`/play/slot/${mine.slot_id}`); return; }
      const list = root.querySelector<HTMLElement>("[data-players]")!;
      const keep = new Set(result.seeks.map(s => s.id));
      list.querySelectorAll<HTMLElement>('[data-row]').forEach(row => { if (!keep.has(row.dataset.row!)) row.remove(); });
      list.querySelector('[data-empty]')?.remove();
      for (const seek of result.seeks) {
        let row = [...list.querySelectorAll<HTMLElement>('[data-row]')].find(row => row.dataset.row === seek.id);
        if (!row) {
          row = document.createElement('div'); row.className = 'club-row'; row.dataset.row = seek.id;
          const name = document.createElement('span');
          const button = document.createElement('button'); button.className = 'btn'; button.dataset.seek = seek.id; button.textContent = 'Play';
          button.onclick = () => void search(seek.id); row.append(name, button); list.append(row);
        }
        row.querySelector('span')!.textContent = seek.name;
        row.querySelector('button')!.disabled = busy || (player !== undefined && !connection.id) || mine?.state === 'waiting';
      }
      if (!result.seeks.length) {
        const empty = document.createElement('p'); empty.dataset.empty = ''; empty.textContent = 'No other players are waiting right now. Invite a friend, or leave your search open.'; list.append(empty);
      }
      render();
    } catch (error) { message.textContent = error instanceof Error ? error.message : "Please retry.";sessionRecovery(root,error); }
    finally { loading = false; }
  }
  async function search(target?: string) {
    if (busy || dead) return;
    busy = true; hiddenSearch = false; render(); message.textContent = "";
    try {
      if (!player) await establish();
      // This click supplies search intent; only its live ACK permits submission.
      const deadline = Date.now()+8000;
      while (!connection.id && !dead && Date.now()<deadline) await new Promise(resolve => setTimeout(resolve,100));
      if (dead || !player) return;
      if (!connection.id) throw new Error("Could not establish a live connection. Please retry.");
      const result = await new PlayJournal(player.id,"seek-create").send("/api/play/seeks",{actor_id:player.id,command_id:commandId(),connection_id:connection.id,terms:{variant:"standard",clock_mode:"untimed",rated:false},...(target ? {target_id:target}: {})});
      if (result.outcome === "rejected") message.textContent = outcomeMessage(result.code);
      if (result.seek?.state === "reserved" && result.seek.slot_id) navigate(`/play/slot/${result.seek.slot_id}`);
    } catch (error) { message.textContent = error instanceof Error ? error.message : "Please retry.";sessionRecovery(root,error); }
    finally { busy = false; render(); void refresh(); }
  }
  find.onclick = () => void search();
  cancel.onclick = async () => {
    if (!player || !mine || busy) return;
    busy = true; render();
    try {
      await new PlayJournal(player.id,`seek:${mine.id}`).send(`/api/play/seeks/${mine.id}/commands`,{actor_id:player.id,command_id:commandId(),expected_revision:mine.revision,action:{type:"cancel"}});
      mine = null;
    } catch (error) { message.textContent = error instanceof Error ? error.message : "Please retry."; sessionRecovery(root,error); }
    finally { busy = false; render(); void refresh(); }
  };
  requestAnimationFrame(() => {
    if (!root.isConnected) return;
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void refresh(); },10000);
    const visibility = () => { if (document.visibilityState !== 'visible' && mine?.state === 'waiting') hiddenSearch = true; else if (document.visibilityState === 'visible') void refresh(); };
    document.addEventListener('visibilitychange', visibility);
    onDisconnect(root,() => { dead = true; clearInterval(timer); document.removeEventListener("visibilitychange", visibility); connection.close(); });
    // Reading the available list is passive: a session/lease is not a seek.
    void readPlayer().then(p => {
      if (dead) return;
      if (p) { player = p; connection.start(); }
      return refresh();
    }).catch(error => { message.textContent = error instanceof Error ? error.message : "Please retry.";sessionRecovery(root,error); });
  });
  return root;
}
